/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { BuildOutlined } from '@ant-design/icons';

import { NodeConfig } from '@/client';
import { GuardedButton } from '@/components/GuardedButton';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Card } from '@/primitives/Card';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

export type SelectedNodeConfigCardProps = {
  nodeConfig: NodeConfig;
  onChangeClick: () => void;
};

export function SelectedNodeConfigCard({ nodeConfig, onChangeClick }: SelectedNodeConfigCardProps) {
  const { t } = useMimicTranslation('configWizardOverlay');

  return (
    <Card dataTestId="config-wizard-node-config" padding="sm" bordered>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '0.8em' }}>
        <Text type="subtle" strong size="md">
          {t('configuration')}
        </Text>
        <Flex vertical full gap="xxs">
          <Flex full justify="space-between">
            <Title style={{ fontWeight: 500 }} level={4}>
              <BuildOutlined /> {nodeConfig?.name}
            </Title>
            <GuardedButton
              requiredRole="admin"
              dataTestId="config-wizard-node-config-change"
              type="link"
              onClick={onChangeClick}
            >
              {t('change')}
            </GuardedButton>
          </Flex>
          <Text copyable mono size="md" type="subtle">
            {nodeConfig?.id}
          </Text>
        </Flex>
      </div>
    </Card>
  );
}
